import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { Box } from "@memorang/ui";
import { ModuleActionButton } from "components/ModuleActionButton";
import { ModuleMetadataChips } from "components/ModuleMetadataChips";
import { Avatar, Card, Text } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig } from "../constants";
import { ModuleThumbnail } from "./ModuleThumbnail";

export type ModuleListItemProps = {
	title: string;
	subtitle: string;
	statusLabel?: string;
	status: EXAM_STATUS;
	cta: string;
	thumbnailUrl: string;
	thumbnailSource?: string | number | { uri: string };
	locked?: boolean;
	disabled?: boolean;
	onPress?: () => void;
	loading?: boolean;
	typeLabel?: string;
	numItems?: number;
	duration?: number;
	durationLabel?: string;
	isTest?: boolean;
	testKind?: "pretest" | "posttest";
	icon?: string;
};

export const ModuleListItem = ({
	title,
	subtitle,
	status,
	cta,
	thumbnailUrl,
	thumbnailSource,
	locked,
	disabled,
	onPress = () => undefined,
	loading,
	duration,
	durationLabel,
	typeLabel,
	isTest,
	testKind,
	icon,
}: ModuleListItemProps) => {
	const { isMobile } = useDevice();
	const theme = useAppTheme();

	const { buttonMode, buttonIcon } = buttonConfig[status];
	const finalDurationLabel =
		duration !== undefined ? (durationLabel ?? `${duration} min`) : undefined;
	const metadataChipProps = isTest
		? { typeLabel }
		: { durationLabel: finalDurationLabel };

	return (
		<Card
			mode="outlined"
			onPress={disabled ? undefined : onPress}
			style={{
				borderRadius: theme.roundness,
				overflow: "hidden",
				backgroundColor: theme.colors.surface,
				borderColor: theme.colors.outlineVariant,
				marginHorizontal: isMobile ? 16 : 0,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={12}
				style={{ gap: 12 }}
			>
				{/* Leading Media / Thumbnail */}
				<ModuleThumbnail
					size={72}
					title={title}
					thumbnailSource={thumbnailSource}
					thumbnailUrl={thumbnailUrl}
					locked={locked}
					isTest={isTest}
					testKind={testKind}
					icon={icon}
				/>

				{/* Title and Content */}
				<Box flex={1} style={{ gap: 4 }}>
					<Text
						variant="titleSmall"
						style={{
							fontWeight: "bold",
							color: theme.colors.onSurface,
						}}
						numberOfLines={2}
					>
						{title}
					</Text>
					<Text
						variant="bodySmall"
						style={{
							color: theme.colors.onSurfaceVariant,
						}}
						numberOfLines={2}
					>
						{subtitle}
					</Text>
					<ModuleMetadataChips
						{...metadataChipProps}
						style={{ marginTop: 8 }}
					/>
				</Box>

				{/* Trailing Action */}
				<Box justifyContent="center" paddingLeft={4}>
					{locked ? (
						<Avatar.Icon
							size={32}
							icon="crown-outline"
							style={{ backgroundColor: "transparent" }}
							color={theme.colors.primary}
						/>
					) : (
						<ModuleActionButton
							mode={buttonMode}
							icon={buttonIcon}
							cta={cta}
							loading={loading}
							disabled={loading || disabled}
							compact={isMobile}
							onPress={onPress}
						/>
					)}
				</Box>
			</Box>
		</Card>
	);
};
